import React from "react";

import InfoTooltip from "src/components/InfoTooltip";
import { valueGetterWithDefault } from "src/utils/valueGetter";
import {
  formatAbsCurrency,
  formatCurrency,
  formatProfitCurrency,
} from "src/utils/formatter";
import {
  makeSalesStyle,
  makeTransactionsStyle,
  makeProfitStyle,
} from "src/utils/gridCellStyles";
import vendorRenderCell from "src/utils/vendorRenderCell";
import renderHeaderDescriptionLabel from "src/utils/renderHeaderLabel";
import makeRenderCell from "src/utils/makeRenderCell";
import { calculateGrossProfit } from "src/utils/sales";
import { withGridStringOperatorsWithNotContains } from "src/utils/gridStringFilters";
import { Columns as TransactionColumns } from "../All";
import { Columns as ExpenseColumns } from "../Expenses";

const TableColumnInfo = {
  location: "Where you have stored the item.",
  purchase_date: "The date that you purchased this item.",
  list_date: "The date that you listed this item for sale.",
  sale_date: "The date that you sold this item.",
  days_on_platform:
    "The number of days the item was listed before selling. In other words ‘Sale Date’ – ‘List Date’.",
  sale_price:
    "The total revenue collected from a customer. This includes the price of the item and additional money collected for shipping. This does not include sales tax.",
  purchase_price: "The amount you paid for the inventory.",
  shipping_cost: "The amount you paid to ship this sale",
  transaction_fees: "The amount the platform charged you to sell the item.",
  other_fees: "Other expenses attached to the sale",
  gross_profit:
    "Gross Profit = Sale Price - Purchase Price - Shipping Costs - Transaction Fees - Other Fees",
  sales_tax: "The sales tax paid by the buyer.",
  liable_to_pay:
    "If you aren’t responsible for filing sales tax, mark this field as ‘No’. If you are responsible for filing sales tax, mark this field as ‘Yes’.",
  notes: "Enter any other information that you need in here.",
};

export function renderHeaderLabel(params) {
  const tooltip = params.colDef.title || TableColumnInfo[params.field];
  return (
    <div className="MuiDataGrid-columnHeaderTitle">
      {params.colDef.headerName}
      {!!tooltip && <InfoTooltip size="small" text={tooltip} />}
    </div>
  );
}

export const Columns = withGridStringOperatorsWithNotContains([
  {
    field: "id",
    hide: true,
  },
  {
    field: "transaction_id",
    headerName: "Platform ID",
    width: 150,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "item_title",
    headerName: "Item Title",
    width: 200,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "department",
    headerName: "Department",
    width: 150,
    renderHeader: renderHeaderLabel,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "category",
    headerName: "Category",
    width: 200,
    renderHeader: renderHeaderLabel,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "sub_category",
    headerName: "Sub Category",
    width: 200,
    renderHeader: renderHeaderLabel,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "vendor",
    headerName: "Vendor",
    width: 200,
    renderCell: vendorRenderCell,
    renderHeader: renderHeaderDescriptionLabel,
    description: "The vendor is where you purchased the item (ex. Goodwill).",
    valueGetter: (params) => params.row.vendorName || params.value || "",
  },
  {
    field: "brand",
    headerName: "Brand",
    width: 150,
    renderHeader: renderHeaderLabel,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "location",
    headerName: "Location",
    width: 200,
    renderHeader: renderHeaderLabel,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "sku",
    headerName: "SKU",
    width: 150,
    renderHeader: renderHeaderLabel,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "purchase_date",
    headerName: "Purchase Date",
    type: "date",
    width: 200,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "list_date",
    headerName: "List Date",
    type: "date",
    width: 200,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "sale_date",
    headerName: "Sale Date",
    type: "date",
    width: 200,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "days_on_platform",
    headerName: "Days On Platform",
    type: "number",
    width: 220,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "sale_price",
    headerName: "Sale Price",
    type: "number",
    width: 200,
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeSalesStyle,
    }),
  },
  {
    field: "purchase_price",
    headerName: "Purchase Price",
    type: "number",
    width: 220,
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeTransactionsStyle,
    }),
  },
  {
    field: "shipping_cost",
    headerName: "Shipping Costs",
    type: "number",
    width: 200,
    valueGetter: (params) =>
      (params.value || 0) + (params.row.shipping_cost_analytics || 0),
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeTransactionsStyle,
    }),
  },
  {
    field: "transaction_fees",
    headerName: "Transaction Fees",
    type: "number",
    width: 220,
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeTransactionsStyle,
    }),
  },
  {
    field: "other_fees",
    headerName: "Other Fees",
    type: "number",
    width: 285,
    valueFormatter: formatCurrency,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "gross_profit",
    headerName: "Gross profit",
    type: "number",
    width: 200,
    valueGetter: (params) => calculateGrossProfit(params.row),
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatProfitCurrency,
      style: makeProfitStyle,
    }),
  },
  {
    field: "sale_state",
    headerName: "Sale State",
    width: 200,
    renderHeader: renderHeaderLabel,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "sales_tax",
    headerName: "Sales Tax",
    type: "number",
    width: 200,
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeSalesStyle,
    }),
  },
  {
    field: "liable_to_pay",
    headerName: "Liable To Pay",
    width: 200,
    type: "boolean",
    renderHeader: renderHeaderLabel,
  },
  {
    field: "platforms_listed",
    headerName: "Platforms Listed",
    width: 300,
    renderHeader: renderHeaderLabel,
    valueFormatter: ({ value }) => {
      if (Array.isArray(value) && value.length) {
        return `${value.join(", ")}`;
      }
      return "";
    },
  },
  {
    field: "sale_platform",
    headerName: "Sale Platform",
    width: 200,
    renderHeader: renderHeaderLabel,
  },
  {
    field: "notes",
    headerName: "Notes",
    width: 200,
    renderHeader: renderHeaderLabel,
    valueGetter: valueGetterWithDefault(""),
  },
  {
    field: "status",
    headerName: "Status",
    width: 125,
    valueGetter: (params) => {
      if (params.row.in_process === true) return "In Process";
      return "Complete";
    },
  },
]);

export const ReturnColumns = [
  {
    field: "id",
    sortable: false,
    filterable: false,
    hide: true,
  },
  {
    field: "transaction_id",
    headerName: "Platform ID",
    width: 200,
    valueGetter: valueGetterWithDefault(""),
  },
  ...Columns.slice(2, 12),
  {
    field: "return_date",
    headerName: "Return Date",
    type: "date",
    width: 200,
    renderHeader: renderHeaderLabel,
  },
  ...Columns.slice(12, 17),
  {
    field: "extra_shipping_cost",
    headerName: "Return Shipping Cost",
    type: "number",
    width: 200,
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeTransactionsStyle,
    }),
  },
  {
    field: "extra_transaction_fees",
    headerName: "Return Transaction Fees",
    type: "number",
    width: 200,
    renderHeader: renderHeaderLabel,
    renderCell: makeRenderCell({
      valueFormatter: formatAbsCurrency,
      style: makeTransactionsStyle,
    }),
  },
  ...Columns.slice(17, 19),
  {
    ...Columns[19],
    title:
      "Gross Profit = Sale Price - Purchase Price - Shipping Costs - Return Shipping Cost - Return Transaction Fees - Transaction Fees - Other Fees",
  },
  ...Columns.slice(20),
];

const ColumnsCashIgnored = [
  "purchase_date",
  "purchase_price",
  "platforms_listed",
];

export const ColumnsCash = Columns.filter(
  ({ field }) => !ColumnsCashIgnored.includes(field)
);

export const ReturnColumnsCash = ReturnColumns.filter(
  ({ field }) => !ColumnsCashIgnored.includes(field)
);

export const TransactionSalesColumns = TransactionColumns;
const TransactionSalesOtherColumnsOrder = [
  "id",
  "date",
  "account",
  "integration_name",
  "name",
  "description",
  "amount",
];
export const TransactionSalesOtherColumns = ExpenseColumns.filter(
  (c) => c.field !== "vendor"
);
TransactionSalesOtherColumns.sort((a, b) => {
  let aIndex = TransactionSalesOtherColumnsOrder.indexOf(a.field);
  if (aIndex < 0) aIndex = TransactionSalesOtherColumnsOrder.length;
  let bIndex = TransactionSalesOtherColumnsOrder.indexOf(b.field);
  if (bIndex < 0) bIndex = TransactionSalesOtherColumnsOrder.length;
  return aIndex - bIndex;
});
